import { Link } from "@tanstack/react-router";
import { m as Motion, useReducedMotion } from "framer-motion";
import { useId, type FormEvent, type ReactNode } from "react";
import {
  ArrowLeft01Icon,
  ArrowRight01Icon,
  LockKeyIcon,
  Mail01Icon,
  Wallet01Icon,
} from "~/components/icons/central-icons";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import type { LoginController } from "~/features/login/state/use-login-controller";
import { getPublicAssetUrl } from "~/lib/env";
import { m } from "~/lib/i18n";
import { cn } from "~/lib/utils";
import "./login-page.css";

function MethodDivider() {
  return (
    <div className="glider-login__divider">
      <span />
      <span>{m.login_page_or_label()}</span>
      <span />
    </div>
  );
}

function LoginError(props: { readonly message: string | null }) {
  if (!props.message) {
    return null;
  }

  return (
    <p className="glider-login__error" role="alert">
      {props.message}
    </p>
  );
}

interface EmailCodeFormProps {
  readonly codeInputId: string;
  readonly controller: LoginController;
}

function EmailCodeForm(props: EmailCodeFormProps) {
  const { controller } = props;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void controller.submitVerificationCode();
  };

  return (
    <form className="glider-login__form" onSubmit={handleSubmit}>
      <label htmlFor={props.codeInputId}>{m.login_page_code_label()}</label>
      <input
        aria-label={m.login_page_code_label()}
        autoComplete="one-time-code"
        autoFocus
        disabled={controller.isBusy}
        id={props.codeInputId}
        inputMode="numeric"
        maxLength={6}
        onChange={(event) =>
          controller.setVerificationCode(
            event.currentTarget.value.replace(/\D/gu, ""),
          )
        }
        pattern="[0-9]{6}"
        placeholder={m.login_page_code_placeholder()}
        required
        type="text"
        value={controller.verificationCode}
      />
      <button
        className="glider-login__primary-button"
        disabled={controller.isBusy || controller.verificationCode.length !== 6}
        type="submit"
      >
        {controller.authAction === "email-code"
          ? m.login_page_verifying_label()
          : m.login_page_verify_label()}
        <ArrowRight01Icon aria-hidden="true" className="size-4" />
      </button>
      <button
        className="glider-login__text-button"
        disabled={controller.isBusy}
        onClick={() => void controller.resendEmailCode()}
        type="button"
      >
        {m.login_page_resend_code_label()}
      </button>
      <LoginError message={controller.errorMessage} />
    </form>
  );
}

interface LoginMethodsProps {
  readonly controller: LoginController;
  readonly emailInputId: string;
}

function LoginMethods(props: LoginMethodsProps) {
  const { controller } = props;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void controller.submitEmail();
  };

  return (
    <div className="glider-login__methods">
      <button
        className="glider-login__google-button"
        disabled={controller.isBusy}
        onClick={() => void controller.loginWithGoogle()}
        type="button"
      >
        <span aria-hidden="true" className="glider-login__google-mark">
          {/* i18n-exempt: Google provider monogram */}G
        </span>
        {controller.authAction === "google"
          ? m.login_page_google_loading_label()
          : m.login_page_google_label()}
      </button>

      <MethodDivider />

      <form className="glider-login__form" onSubmit={handleSubmit}>
        <label htmlFor={props.emailInputId}>{m.login_page_email_label()}</label>
        <div className="glider-login__input-wrap">
          <Mail01Icon aria-hidden="true" className="size-4" />
          <input
            aria-label={m.login_page_email_label()}
            autoComplete="email"
            disabled={controller.isBusy}
            id={props.emailInputId}
            onChange={(event) => controller.setEmail(event.currentTarget.value)}
            placeholder={m.login_page_email_placeholder()}
            required
            type="email"
            value={controller.email}
          />
        </div>
        <button
          className="glider-login__primary-button"
          disabled={controller.isBusy || controller.email.trim().length === 0}
          type="submit"
        >
          {controller.authAction === "email-send"
            ? m.login_page_sending_code_label()
            : m.login_page_email_continue_label()}
          <ArrowRight01Icon aria-hidden="true" className="size-4" />
        </button>
      </form>

      <MethodDivider />

      <button
        className="glider-login__wallet-button"
        disabled={controller.isBusy}
        onClick={controller.enterWalletLogin}
        type="button"
      >
        <Wallet01Icon aria-hidden="true" className="size-4" />
        {m.login_page_wallet_label()}
      </button>
      <LoginError message={controller.errorMessage} />
    </div>
  );
}

function CompactEmailCodeForm(props: EmailCodeFormProps) {
  const { controller } = props;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void controller.submitVerificationCode();
  };

  return (
    <>
      <form className="glider-login-compact__input-row" onSubmit={handleSubmit}>
        <Input
          aria-label={m.login_page_code_label()}
          autoComplete="one-time-code"
          autoFocus
          className="glider-login-compact__input"
          disabled={controller.isBusy}
          id={props.codeInputId}
          inputMode="numeric"
          maxLength={6}
          onChange={(event) =>
            controller.setVerificationCode(
              event.currentTarget.value.replace(/\D/gu, ""),
            )
          }
          pattern="[0-9]{6}"
          placeholder={m.login_page_code_placeholder()}
          required
          type="text"
          value={controller.verificationCode}
        />
        <Button
          aria-label={m.login_page_verify_label()}
          className="glider-login-compact__arrow-button"
          disabled={
            controller.isBusy || controller.verificationCode.length !== 6
          }
          loading={controller.authAction === "email-code"}
          size="icon-sm"
          type="submit"
          variant="ghost"
        >
          <ArrowRight01Icon aria-hidden="true" />
        </Button>
      </form>
      <Button
        className="glider-login-compact__resend"
        disabled={controller.isBusy}
        onClick={() => void controller.resendEmailCode()}
        type="button"
        variant="link"
      >
        {m.login_page_resend_code_label()}
      </Button>
      <LoginError message={controller.errorMessage} />
    </>
  );
}

function CompactLoginMethods(props: LoginMethodsProps) {
  const { controller } = props;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void controller.submitEmail();
  };

  return (
    <div className="glider-login-compact__methods">
      <form className="glider-login-compact__input-row" onSubmit={handleSubmit}>
        <Input
          aria-label={m.login_page_email_label()}
          autoComplete="email"
          className="glider-login-compact__input"
          disabled={controller.isBusy}
          id={props.emailInputId}
          onChange={(event) => controller.setEmail(event.currentTarget.value)}
          placeholder={m.login_page_email_label()}
          required
          type="email"
          value={controller.email}
        />
        <Button
          aria-label={m.login_page_email_continue_label()}
          className="glider-login-compact__arrow-button"
          disabled={controller.isBusy || controller.email.trim().length === 0}
          loading={controller.authAction === "email-send"}
          size="icon-sm"
          type="submit"
          variant="ghost"
        >
          <ArrowRight01Icon aria-hidden="true" />
        </Button>
      </form>

      <div className="glider-login-compact__divider">
        <span />
        <span>{m.login_page_or_label()}</span>
        <span />
      </div>

      <div className="glider-login-compact__provider-row">
        <Button
          aria-label={m.login_page_google_label()}
          className="glider-login-compact__google-button"
          disabled={controller.isBusy}
          loading={controller.authAction === "google"}
          onClick={() => void controller.loginWithGoogle()}
          size="icon-lg"
          type="button"
          variant="ghost"
        >
          <img
            alt=""
            aria-hidden="true"
            height={22}
            src={getPublicAssetUrl("/login/google-mark.svg")}
            width={21}
          />
        </Button>
        <Button
          aria-label={m.login_page_wallet_label()}
          className="glider-login-compact__wallet-button"
          disabled={controller.isBusy}
          onClick={controller.enterWalletLogin}
          type="button"
          variant="ghost"
        >
          {m.onboarding_v1_account_wallet_action()}
        </Button>
      </div>
      <LoginError message={controller.errorMessage} />
    </div>
  );
}

type LoginAuthPanelAppearance = "compact" | "default";

export interface LoginAuthPanelProps {
  readonly appearance?: LoginAuthPanelAppearance;
  readonly className?: string;
  readonly controller: LoginController;
  readonly methodsDescription?: ReactNode;
  readonly methodsHeading?: ReactNode;
  readonly reduceMotion?: boolean;
}

export function LoginAuthPanel(props: LoginAuthPanelProps) {
  const generatedId = useId();
  const prefersReducedMotion = useReducedMotion() ?? false;
  const reduceMotion = props.reduceMotion ?? prefersReducedMotion;
  const headingId = `${generatedId}-heading`;
  const emailInputId = `${generatedId}-email`;
  const codeInputId = `${generatedId}-code`;
  const { controller } = props;

  const heading =
    controller.view === "email-code"
      ? m.login_page_code_heading()
      : (props.methodsHeading ?? m.login_page_card_heading());
  const description =
    controller.view === "email-code"
      ? m.login_page_code_description({ email: controller.email })
      : (props.methodsDescription ?? m.login_page_card_description());

  if (props.appearance === "compact") {
    let compactContent: ReactNode;
    if (!controller.ready || controller.authAction === "provisioning") {
      compactContent = (
        <div className="glider-login__preparing" role="status">
          <span aria-hidden="true" className="glider-login__spinner" />
          {controller.authAction === "provisioning"
            ? m.login_page_finishing_label()
            : m.login_page_preparing_label()}
        </div>
      );
    } else if (controller.view === "email-code") {
      compactContent = (
        <CompactEmailCodeForm
          codeInputId={codeInputId}
          controller={controller}
        />
      );
    } else {
      compactContent = (
        <CompactLoginMethods
          controller={controller}
          emailInputId={emailInputId}
        />
      );
    }

    return (
      <Motion.section
        animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
        aria-busy={controller.isBusy}
        aria-labelledby={headingId}
        className={cn(
          "glider-login-auth-panel glider-login-auth-panel--compact",
          props.className,
        )}
        data-appearance="compact"
        initial={
          reduceMotion
            ? { opacity: 1, transform: "translate3d(0, 0, 0)" }
            : { opacity: 0, transform: "translate3d(0, 18px, 0)" }
        }
        transition={{
          delay: reduceMotion ? 0 : 0.06,
          duration: reduceMotion ? 0 : 0.34,
          ease: "easeOut",
        }}
      >
        <div className="glider-login-compact__heading">
          {controller.view !== "methods" ? (
            <Button
              aria-label={m.components_ask_user_questions_back_label()}
              className="glider-login-compact__back-button"
              disabled={controller.isBusy}
              onClick={controller.resetToMethods}
              size="icon-sm"
              type="button"
              variant="ghost"
            >
              <ArrowLeft01Icon aria-hidden="true" />
            </Button>
          ) : null}
          <div>
            <h2 id={headingId}>{heading}</h2>
            {controller.view === "methods" || !description ? null : (
              <p>{description}</p>
            )}
          </div>
        </div>
        {compactContent}
      </Motion.section>
    );
  }

  let content: ReactNode;
  if (!controller.ready || controller.authAction === "provisioning") {
    content = (
      <div className="glider-login__preparing" role="status">
        <span aria-hidden="true" className="glider-login__spinner" />
        {controller.authAction === "provisioning"
          ? m.login_page_finishing_label()
          : m.login_page_preparing_label()}
      </div>
    );
  } else if (controller.view === "email-code") {
    content = (
      <EmailCodeForm codeInputId={codeInputId} controller={controller} />
    );
  } else {
    content = (
      <LoginMethods controller={controller} emailInputId={emailInputId} />
    );
  }

  return (
    <Motion.section
      animate={{ opacity: 1, transform: "translate3d(0, 0, 0)" }}
      aria-busy={controller.isBusy}
      aria-labelledby={headingId}
      className={cn(
        "glider-login__card glider-login-auth-panel",
        props.className,
      )}
      initial={
        reduceMotion
          ? { opacity: 1, transform: "translate3d(0, 0, 0)" }
          : { opacity: 0, transform: "translate3d(18px, 0, 0)" }
      }
      transition={{
        delay: reduceMotion ? 0 : 0.06,
        duration: reduceMotion ? 0 : 0.34,
        ease: "easeOut",
      }}
    >
      <div className="glider-login__card-topline">
        <span>{m.login_page_secure_access_label()}</span>
        <span className="glider-login__status-dot">
          {m.login_page_privy_status_label()}
        </span>
      </div>

      <div className="glider-login__card-heading">
        {controller.view !== "methods" ? (
          <button
            aria-label={m.components_ask_user_questions_back_label()}
            className="glider-login__back-button"
            disabled={controller.isBusy}
            onClick={controller.resetToMethods}
            type="button"
          >
            <ArrowLeft01Icon aria-hidden="true" className="size-4" />
          </button>
        ) : null}
        <div>
          <h2 id={headingId}>{heading}</h2>
          <p>{description}</p>
        </div>
      </div>

      {content}

      <p className="glider-login__legal">
        {m.login_page_legal_prefix()}{" "}
        <Link to="/terms">{m.login_page_terms_label()}</Link>{" "}
        {m.login_page_legal_joiner()}{" "}
        <Link to="/policy">{m.login_page_privacy_label()}</Link>.
      </p>

      <div className="glider-login__card-footer">
        <LockKeyIcon aria-hidden="true" className="size-4" />
        <span>{m.login_page_security_note()}</span>
      </div>
    </Motion.section>
  );
}
